import {
  Module,
  Action,
  Mutation,
  VuexModule,
} from 'vuex-module-decorators';
import generatePuzzleFromDate from '@/wordsearch/generator';
import { Vue } from 'vue-property-decorator';
import { getWordOutOfCells, mapCellsIdsToCells } from '@/wordsearch/utils';
import { Cell } from '@/wordsearch/types';
import { getCurrentDateYYYYMMDD } from '@/utils/date-helpers';
import { PopupType } from '@/store/module/ui';
import {
  savePuzzleProgress,
  getSavedPuzzleProgress,
  saveCompletedDates,
  getSavedCompletedDates,
  SaveProgressData,
  savePuzzleInfo,
} from '@/utils/save-load-data';
import { isIOS, isMobile, isTablet } from 'mobile-device-detect';
import { musicOnOff, pauseSound, playSound } from '@/utils/playSound';

type UserAnswers = Record<string, string[]>;

declare const sdkHandler: any;

@Module({ namespaced: true })
class Puzzle extends VuexModule {
  public puzzle = generatePuzzleFromDate(getCurrentDateYYYYMMDD());

  public hintsUsed = 0;

  public tempDate: string = getCurrentDateYYYYMMDD();

  // TODO: check hi res
  public gridSize = 500;

  public puzzleTime = 0;

  public date: string = getCurrentDateYYYYMMDD();

  public userAnswers: UserAnswers = {};

  public selectedCells: string[] = [];

  public hintCell: string | null = null;

  public completedDates: string[] = [];

  get selectedLetters() {
    return mapCellsIdsToCells(this.puzzle, this.selectedCells).map((cell) => cell.letter).join('');
  }

  get cellWidth() {
    return this.gridSize / this.puzzle.width;
  }

  get cellHeight() {
    return this.gridSize / this.puzzle.width;
  }

  get solved() {
    return Object.keys(this.userAnswers).length === this.puzzle.words.length;
  }

  // TODO: do type for CellId
  get cellsForHint(): string[] {
    const alreadyFound = Object.keys(this.userAnswers);
    return Object.keys(this.puzzle.answers)
      .filter((word) => !alreadyFound.includes(word))
      .map((word) => this.puzzle.answers[word][0]);
  }

  get paused() {
    const { popup } = this.context.rootState.UI;
    const { innerWidth, innerHeight } = window;

    return Boolean(
      this.solved
      || (popup && popup !== PopupType.Victory)
      || this.context.rootState.UI.outOfFocus
    );
  }

  @Mutation
  public setCompletedDates(completedDates: string[]): void {
    this.completedDates = completedDates;
  }

  @Mutation
  public addCompletedDate(date: string): void {
    if (this.completedDates.includes(date)) return;
    this.completedDates.push(date);
    saveCompletedDates(this.completedDates);
  }

  @Mutation
  public increasePuzzleTime(): void {
    this.puzzleTime += 1;
    savePuzzleProgress(this.date, this.userAnswers, this.puzzleTime, this.hintsUsed);
  }

  @Mutation
  public increaseHintsUsed(): void {
    this.hintsUsed += 1;
    savePuzzleProgress(this.date, this.userAnswers, this.puzzleTime, this.hintsUsed);
  }

  @Mutation
  public setHintCell(cell: string | null): void {
    this.hintCell = cell;
  }

  @Mutation
  public setSelection(cellsIds: string[]): void {
    this.selectedCells = cellsIds;
  }

  @Mutation
  public addAnswer(cells: Cell[]): void {
    let word = getWordOutOfCells(cells);
    const reversedWord = word.split("").reverse().join("");
    if (this.puzzle.words.includes(reversedWord)) {
      word = reversedWord;
    }
    Vue.set(this.userAnswers, word, cells.map((cell) => cell.id));
  }

  @Mutation
  public setGridSize(size: number): void {
    this.gridSize = size;
  }

  @Mutation
  public clearPuzzle(): void {
    console.log('clearPuzzle', this);
    this.userAnswers = {};
    this.puzzleTime = 0;
    this.hintsUsed = 0;
    savePuzzleProgress(this.date, {}, 0, 0);
  }

  @Mutation
  public setNewPuzzleFromDate(date: string): void {
    this.puzzle = generatePuzzleFromDate(date);
    this.userAnswers = {};
    this.puzzleTime = 0;
    this.hintsUsed = 0;
    this.date = date;
  }

  @Mutation
  public setPuzzleProgress({ time, userAnswers, hintsUsed }: SaveProgressData): void {
    this.puzzleTime = time;
    this.userAnswers = userAnswers;
    this.hintsUsed = hintsUsed;
  }

  @Mutation
  public setTempDate(date: string): void {
    this.tempDate = date;
  }

  @Action
  public changePuzzleByDate(date: string): void {
    (window as any).sdkHandler.trigger('gameOver', {
      score: this.puzzleTime,
      callback: () => {
        // do nothing
      },
    });

    this.context.commit('setNewPuzzleFromDate', date);
    this.context.commit('setPuzzleProgress', getSavedPuzzleProgress(date));
    savePuzzleInfo(this.date, Object.keys(this.puzzle.answers).length);

    (window as any).sdkHandler.trigger('gameStart');

    setTimeout(() => {
      const wordContainer = document.getElementsByClassName('wordList--columnWrapper')[0] as HTMLElement;
      if (wordContainer) {
        let initialFontSize = 1;
        if (isTablet || (innerWidth / innerHeight * 100 > 64 && !isIOS)) {
          initialFontSize = 1.5;
        }
        wordContainer.style.fontSize = `${initialFontSize}em`;
        const elements = document.getElementsByClassName('word');
        for (let i = 0; i < elements.length; i++) {
          const element = elements[i];
          if (element) {
            let ratio = element.scrollWidth / element.clientWidth;
            if (ratio > 1) {
              ratio *= 1.1;
              wordContainer.style.fontSize = `${initialFontSize / ratio}em`;
            }
          }
        }
      }
    }, 20);
  }

  @Action
  public handleTempDate(date: Date): void {
    this.context.commit('setTempDate', date);
  }

  @Action
  public submitAnswer(cells: Cell[]): void {
    let word = getWordOutOfCells(cells);
    const reversedWord = word.split("").reverse().join("");
    if (this.puzzle.words.includes(reversedWord)) {
      word = reversedWord;
    }
    console.log('submitAnswer', word);
    if (!this.puzzle.words.includes(word)) return;
    if (this.userAnswers[word]) return;
    playSound('wordMatchSound');
    this.context.commit('addAnswer', cells);

    if (this.solved) {
      playSound('winSound');
      this.context.dispatch('UI/openVictoryPopup', undefined, { root: true });
      this.context.commit('addCompletedDate', this.date);
    }

    savePuzzleProgress(this.date, this.userAnswers, this.puzzleTime, this.hintsUsed);
  }

  @Action
  public tickTimer(): void {
    if (this.paused) return;
    this.context.commit('increasePuzzleTime');
  }

  @Action({ rawError: true })
  public revealPuzzle(): void {
    const userAnswers = Object.keys(this.userAnswers);

    Object.entries(this.puzzle.answers).forEach(([answer, cellsIds]) => {
      if (userAnswers.includes(answer)) return;
      this.context.dispatch('submitAnswer', mapCellsIdsToCells(this.puzzle, cellsIds));
    });
  }

  @Action
  public showHint(): void {
    this.context.commit('increaseHintsUsed');
    const cell = this.cellsForHint[Math.floor(Math.random() * (this.cellsForHint.length - 1))];
    this.context.commit('setHintCell', cell);
    // setTimeout(() => {
    //   if (this.hintCell === cell) this.context.commit('setHintCell', null);
    // }, 3000);
  }

  @Action
  public init(): void {
    this.context.commit('setCompletedDates', getSavedCompletedDates());
    this.context.commit('setPuzzleProgress', getSavedPuzzleProgress(this.date));
    savePuzzleInfo(this.date, Object.keys(this.puzzle.answers).length);
    window.addEventListener("blur", () => {
      pauseSound('musicSound');
      this.context.rootState.UI.outOfFocus = true;
    });
    window.addEventListener("focus", () => {
      if (musicOnOff.musicOn) playSound('musicSound');
      this.context.rootState.UI.outOfFocus = false;
    });
  }
}

export default Puzzle;
